import type Luminary from '@lumen5/luminary';
import type { TextAsset } from '@lumen5/luminary';
import type { GetRootState } from 'js/src/libs/RootState';
import { VideoData } from '@lumen5/luminary';

import { TOOL_NAMES } from 'js/src/agent/toolNames';

import { getNonRecommendableAssetNames } from '../../video-state/serialization/assetSerializer';
import type { LintFinding } from '../videoLinter';

/**
 * Error: a text asset's rendered text overflows its `maxWidth × maxHeight`
 * bounds, so part of it is clipped off at the box edge and never shown. This is
 * the same condition the creator surfaces with a persistent clip indicator next
 * to the asset.
 */
export function checkTextClipping(luminary: Luminary, getState: GetRootState): LintFinding[] {
    const findings: LintFinding[] = [];

    luminary.scenes.forEach((scene) => {
        const nonRecommendable = getNonRecommendableAssetNames(scene, getState());
        const textAssets = scene
            .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
            .filter(
                (asset) =>
                    asset.isEditable && asset.text?.trim() && !nonRecommendable.has(asset.name),
            );

        textAssets.forEach((asset) => {
            if (!asset.isClipping) {
                return;
            }
            const text = asset.text.trim();
            findings.push({
                check: 'text_clipping',
                severity: 'error',
                sceneId: scene.id,
                assetName: asset.name,
                message: `Text "${text}" is clipped at its box's edge, so part of it is cut off. ${describeRemedy(asset)}`,
            });
        });
    });

    return findings;
}

function describeRemedy(asset: TextAsset): string {
    const heightGovernor = asset.heightGovernor;
    const governingGroup = VideoData.isAssetGroup(heightGovernor) ? heightGovernor : undefined;

    const remedies =
        'shorten the text, lower its font size, widen the box, or swap the scene for a design that holds more text';

    if (governingGroup && governingGroup.isHeightHeld) {
        return `Its group "${governingGroup.name}" holds the height, so a taller height on this box alone will not help: ${remedies}, or give that group a taller max_height with ${TOOL_NAMES.setGroupAsset}.`;
    }

    if (!governingGroup && asset.isHeightHeld) {
        return `The box holds a height of its own: ${remedies}, or give it a taller height with ${TOOL_NAMES.setText}.`;
    }

    return `The text is taller than the whole frame, so no box can show it: ${remedies}.`;
}
